import { useGetUser, useUpdateUserMutation } from "@hooks";
import { TextField, Typography } from "@mui/material";
import { captureSentryMessage } from "@utils";
import { Dispatch, SetStateAction, useState } from "react";
import { useParams } from "react-router-dom";
import Start from "src/components/Onboarding/Start";
import { steps } from "src/components/Onboarding/step-config";
import WelcomeSkeleton from "src/components/Onboarding/WelcomeSkeleton";
import { getOrgFromUser } from "src/utils/user";
import { RetroGrid } from "../AnimatedGridPattern";
import GenericButton from "../GenericButton/GenericButton";
import ProgressBar from "./ProgressBar";

export const Step = ({
  closeOnboarding,
  step,
  setStep,
}: {
  closeOnboarding: () => void;
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
}) => {
  const { orgId = "" } = useParams();
  const { data: user } = useGetUser();
  const org = getOrgFromUser(user, orgId);

  const patchUserMutation = useUpdateUserMutation(orgId);

  const [shouldIgnoreUserNameStep, setShouldIgnoreUserNameStep] =
    useState(false);

  const handleNextStep = () => {
    // skip to end if user already has a name
    if (step === 0 && hasUserName) {
      setShouldIgnoreUserNameStep(true);
      closeOnboarding();
      return;
    }

    setStep(step + 1);
  };

  const currentStep = steps[step];


  const isSurveyStep = currentStep.id.includes("survey");
  const isLastStep = step === steps.length - 1;

  const totalSurveySteps = steps.filter((s) => s.id.includes("survey")).length;
  const currentSurveyStep = isSurveyStep
    ? steps
        .filter((s) => s.id.includes("survey"))
        .findIndex((s) => s.id === currentStep.id) + 1
    : 0;

  if (!currentStep) {
    captureSentryMessage("Current step not found");
    return null;
  }

  const currentProgress = isSurveyStep
    ? ((currentSurveyStep - (shouldIgnoreUserNameStep ? 1 : 0)) /
        (totalSurveySteps - (shouldIgnoreUserNameStep ? 1 : 0))) *
      100
    : undefined;

  const [userName, setUserName] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
  });

  const handleSave = (userId: string) => {
    if (currentStep.id === "survey-name") {
      patchUserMutation.mutate({
        id: userId,
        user: { ...userName },
      });
    }

    if (isLastStep) {
      closeOnboarding();
      return;
    }

    handleNextStep();
  };

  const handleChange = (
    fieldId: string,
    v: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUserName({
      ...userName,
      [fieldId]: v.currentTarget.value,
    });
  };

  const isInitialStep = currentStep.id === "start";
  const isNameStep = currentStep.id === "survey-name";
  const isNameComplete =
    userName.firstName.trim().length > 0 && userName.lastName.trim().length > 0;
  const isNextDisabled = isNameStep && !isNameComplete;

  const nextButtonText = currentStep.buttonText || "Next";

  if (!user || !org) {
    return <WelcomeSkeleton />;
  }

  const hasUserName = !!user.firstName && !!user.lastName;

  return (
    <div
      className={`flex flex-1 flex-col p-16 justify-center gap-12 transition-transform origin-center duration-500`}
    >
      {step === 0 && <RetroGrid />}
      <div
        key={currentStep.id}
        className="flex flex-1 flex-col justify-center gap-8"
      >
        {currentProgress !== undefined && (
          <ProgressBar progress={currentProgress} />
        )}
        {currentStep.title && (
          <div className="flex flex-col gap-3">
            <Typography variant={currentStep.titleLevel || "h6"}>
              {currentStep.title}
            </Typography>
            {currentStep.subtitle && (
              <Typography variant="body1" color="text.secondary">
                {currentStep.subtitle}
              </Typography>
            )}
          </div>
        )}

        {currentStep.inputFields?.map(({ element, fieldId, label }) => {
          switch (true) {
            case element === "input-group":
              return (
                <TextField
                  key={fieldId}
                  id={fieldId}
                  label={label}
                  onChange={(v) => handleChange(fieldId, v)}
                  value={userName[fieldId as keyof typeof userName]}
                  className="w-2/5"
                />
              );
          }
        })}

        {isInitialStep && (
          <Start step={currentStep} handleNextStep={handleNextStep} />
        )}

        <div
          className={`p-0 ${currentStep.buttonAlign === "right" ? "self-end" : "self-start"}`}
        >
          {!currentStep.ignoreModalActions && (
            <GenericButton
              id="basic-button"
              variant="contained"
              onClick={() => handleSave(user.id)}
              text={nextButtonText}
              color="primary"
              isDisabled={isNextDisabled}
            />
          )}
        </div>
      </div>
    </div>
  );
};
